import {
  type CurrentPlanPricing,
  type PlanPricing,
  type Subscription,
  type SubscriptionBillingCycle,
  type SubscriptionPlan,
} from '@air/api/types';
import { MONTHS_IN_YEAR } from '@air/utilities';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { formatCreditCount, isEligibleForCreditPacks } from '~/utils/creditUtils';
import { formatPrice } from '~/utils/currencyFormatters';
import { getFixedPlanIncludedStorageBytes } from '~/utils/getFixedPlanIncludedStorageBytes';
import { getPlanFeatures } from '~/utils/getPlanFeatures';
import { subscriptionPlanMap } from '~/utils/SubscriptionUtils';

type Plan = PlanPricing | CurrentPlanPricing;

const isCurrentPlan = (plan: Plan, subscription: Subscription) =>
  plan.id === subscription.plan || ('isCurrentPlan' in plan ? plan.isCurrentPlan : false);

// Active plans available to users for selection
const DEFAULT_VISIBLE_PLANS = ['starter', 'business', 'enterprise'];
const isDefaultVisiblePlan = (plan: Plan) => DEFAULT_VISIBLE_PLANS.includes(plan.id);

// Deprecated/legacy plans that should only appear when they match the current workspace subscription plan
const LEGACY_PLANS = ['plus', 'creator', 'pro'];
const isLegacyPlan = (plan: Plan) => LEGACY_PLANS.includes(plan.id);

const filterPlanList = (plans: Plan[], subscription: Subscription) =>
  plans.filter((plan) => {
    // Show free or deprecated/legacy plan if it matches the current workspace subscription plan
    if ((plan.id === 'basic' || isLegacyPlan(plan)) && isCurrentPlan(plan, subscription)) {
      return true;
    }

    // Otherwise, only show plan if it is currently active/visible (will exclude basic/free plan)
    return isDefaultVisiblePlan(plan);
  });

// Order in which plans should be displayed
const PLAN_SORT_ORDER = ['basic', 'creator', 'starter', 'plus', 'pro', 'business', 'enterprise'];
const sortPlanList = (plans: Plan[]) =>
  plans.sort((a, b) => PLAN_SORT_ORDER.indexOf(a.id) - PLAN_SORT_ORDER.indexOf(b.id));

export type UpgradePlanModalButtonAction =
  | 'current-plan'
  | 'add-credits'
  | 'self-service-downgrade'
  | 'book-demo'
  | 'self-service-upgrade'
  | 'self-service-upgrade-via-checkout';

const getPlanButtonAction = ({
  plan,
  planIndex,
  subscribedPlanIndex,
  subscription,
  creditStats,
}: {
  plan: Plan;
  planIndex: number;
  subscribedPlanIndex: number;
  subscription: Subscription;
  creditStats: CreditUsageStats | undefined;
}): UpgradePlanModalButtonAction => {
  if (planIndex === subscribedPlanIndex) {
    return isEligibleForCreditPacks(subscription, creditStats) ? 'add-credits' : 'current-plan';
  }

  if (planIndex < subscribedPlanIndex) {
    return 'self-service-downgrade';
  }

  if (plan.id === 'enterprise') {
    return 'book-demo';
  }

  return 'self-service-upgrade-via-checkout';
};

// Q3 '26 published enterprise allotment. The plans list and subscription do not return this yet.
const ENTERPRISE_MONTHLY_CREDITS = '4,000+ credits every month';

export const getPlanText = ({
  planId,
  pricingModel,
  limits,
  includedStorage,
  creditsPerCycle,
}: {
  planId: SubscriptionPlan;
  pricingModel: Plan['pricingModel'];
  limits: Plan['limits'];
  includedStorage: Plan['includedStorage'] | null;
  creditsPerCycle: Plan['creditsPerCycle'];
}):
  | {
      tagline?: string;
      credits?: string;
      options: string[];
      customPriceDescription?: string;
      isRecommended?: boolean;
      isLegacy?: boolean;
      secondaryCta?: 'book-demo';
    }
  | undefined => {
  const options =
    getPlanFeatures({ context: 'upgrade', creditsPerCycle, includedStorage, limits, planId, pricingModel }) ?? [];
  const credits = creditsPerCycle ? `${formatCreditCount(creditsPerCycle)} credits every month` : undefined;

  switch (planId) {
    case 'basic':
      return { options, ...((pricingModel === 'credit' || pricingModel === 'fixed') && { credits }) };

    case 'creator':
      return { isLegacy: true, options };

    case 'starter':
      return {
        credits,
        options,
        tagline: 'More space and flexibility for scaling collaboration.',
      };

    case 'plus':
      return { isLegacy: true, options };

    case 'pro':
      return { isLegacy: true, options };

    case 'business': {
      if (pricingModel === 'perMember') {
        return {
          isLegacy: true,
          options,
          tagline: 'Best for teams ready to level up with powerful AI tools.',
        };
      }

      return {
        credits,
        isRecommended: true,
        options,
        secondaryCta: 'book-demo',
        tagline: 'For teams looking to level-up with powerful AI tools.',
      };
    }

    case 'enterprise':
      return {
        credits: credits ?? ENTERPRISE_MONTHLY_CREDITS,
        options,
        tagline: 'Tools for control, security, and support at scale.',
      };
  }
};

const formatPlanInfo = (options: {
  plan: Plan;
  subscription: Subscription;
  creditStats: CreditUsageStats | undefined;
  planIndex: number;
  subscribedPlanIndex: number;
}) => {
  const { plan, subscription } = options;

  const stripePlanIds: Partial<Record<SubscriptionBillingCycle, string>> = {};
  const prices: Partial<Record<SubscriptionBillingCycle, number>> = {};

  plan.billingCycles.forEach((billingCycle) => {
    stripePlanIds[billingCycle.id] = billingCycle.stripeId;
    prices[billingCycle.id] = billingCycle.planPrice;
  });

  const { pricingModel, creditsPerCycle, limits, includedStorage } = isCurrentPlan(plan, subscription)
    ? {
        creditsPerCycle: subscription.creditsPerCycle,
        includedStorage: getFixedPlanIncludedStorageBytes(subscription),
        limits: {
          members: subscription.includedMembers,
          storage: subscription.maxStorage,
        },
        pricingModel: subscription.pricingModel,
      }
    : plan;

  const planText = getPlanText({ creditsPerCycle, includedStorage, limits, planId: plan.id, pricingModel });
  const hasListedPrice = prices.monthly != null || prices.annual != null;

  return {
    action: getPlanButtonAction(options),
    creditsPerCycle,
    isCurrentPlan: options.planIndex === options.subscribedPlanIndex,
    planId: plan.id,
    planTitle: subscriptionPlanMap[plan.id],
    prices,
    pricingModel,
    stripePlanIds,
    ...planText,
    ...(plan.id === 'enterprise' && !hasListedPrice ? { customPriceDescription: 'Custom' } : {}),
  };
};

export type PlanInfoType = ReturnType<typeof formatPlanInfo> & {
  previousPlan: Omit<PlanInfoType, 'previousPlan'> | null;
};

export const getAvailablePlanInfo = ({
  plans,
  subscription,
  creditStats,
}: {
  plans: Plan[];
  subscription: Subscription;
  creditStats: CreditUsageStats | undefined;
}): { currentPlan: PlanInfoType | null; availablePlans: PlanInfoType[] } => {
  const filteredPlans = filterPlanList(plans, subscription);
  const sortedPlans = sortPlanList(filteredPlans);
  const subscribedPlanIndex = sortedPlans.findIndex((plan) => isCurrentPlan(plan, subscription));

  const formattedPlans = sortedPlans.map((plan, index) =>
    formatPlanInfo({ plan, planIndex: index, subscribedPlanIndex, subscription, creditStats }),
  );

  const formattedPlansWithPreviousPlan = formattedPlans.map((plan, index) => {
    let previousPlan = index > 0 ? formattedPlans[index - 1] : null;

    if (previousPlan?.planId === 'basic') {
      previousPlan = null;
    }

    return {
      ...plan,
      previousPlan,
    };
  });

  const { currentPlan, availablePlans } = formattedPlansWithPreviousPlan.reduce<{
    currentPlan: PlanInfoType | null;
    availablePlans: PlanInfoType[];
  }>(
    (acc, plan, index) => {
      if (index === subscribedPlanIndex) {
        if (isEligibleForCreditPacks(subscription, creditStats)) {
          acc.availablePlans.push(plan);
        } else {
          acc.currentPlan = plan;
        }
      }
      // Only show plans of a higher tier than the current plan
      else if (index > subscribedPlanIndex) {
        acc.availablePlans.push(plan);
      }

      return acc;
    },
    { availablePlans: [], currentPlan: null },
  );

  return { availablePlans, currentPlan };
};

export const SUPPORTED_BILLING_CYCLES = ['annual', 'monthly'] satisfies SubscriptionBillingCycle[];

export type UpgradePlanModalBillingCycle = (typeof SUPPORTED_BILLING_CYCLES)[number];

export const isUpgradePlanModalBillingCycle = (value?: string | null): value is UpgradePlanModalBillingCycle => {
  return SUPPORTED_BILLING_CYCLES.some((cycle) => cycle === value);
};

export const formatMonthlyPrice = ({
  prices,
  billingCycle,
}: {
  prices: PlanInfoType['prices'];
  billingCycle: UpgradePlanModalBillingCycle;
}) => {
  if (!prices) {
    return null;
  }

  let amount = prices[billingCycle] ?? 0;

  if (billingCycle === 'annual') {
    amount = Math.ceil(amount / MONTHS_IN_YEAR);
  }

  return formatPrice(amount, { showCents: 'never' });
};
